import {
  Badge,
  BoxV2 as Box,
  ButtonV2 as Button,
  Drawer,
  FilterType,
  Flex,
  Grid,
  InsightCard,
  Table,
  TableFilterV2 as TableFilter,
  useTable,
  useFilters,
  RoleGuard,
} from 'portal-commons';
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  faFontAwesome,
  faSignature,
  faTrademark,
  faPlus,
} from '@fortawesome/pro-regular-svg-icons';

import BrandDetailsV2 from '../BrandDetails/BrandDetailsV2';
import { BrandRegisteredModal, BrandRegistrationV2 } from '../../components';
import { getBadgeTypeByStatus } from '../../utils';
import {
  selectBrandIdentityStatus,
  selectLoaStatus,
  selectTrademarkVerificationStatus,
} from '../../../CSP-Form/selectors';
import { Brand, brandController } from '@/apis/core';
import { getBrandNameAutocompleteOptions } from '@/apis/services';
import { PageContainer, PageTitleV2 as PageTitle } from '@/components';
import { ENTITY_TYPES } from '@/constants';
import { useAuthContext } from '@/contexts';
import { Loader } from '@/shared_elements';
import { humanize } from '@/utils/string';

enum ModalType {
  None = 'none',
  BrandRegistration = 'brandRegistration',
  BrandRegistered = 'brandRegistered',
}

const FilterConfigs = {
  brandUid: {
    type: FilterType.Search,
    label: 'Brand ID',
    placeholder: 'Enter Brand ID',
  },
  displayName: {
    type: FilterType.Search,
    label: 'DBA Name',
    placeholder: 'Enter DBA Name',
    suggestions: [],
  },
  entityType: {
    type: FilterType.Dropdown,
    options: ENTITY_TYPES.map((entity) => ({
      ...entity,
      label: humanize(entity.value),
    })).filter((entity) =>
      ['PRIVATE_PROFIT', 'PUBLIC_PROFIT'].includes(entity.value)
    ),
    label: 'Entity Type',
  },
  identityStatus: {
    type: FilterType.Dropdown,
    options: [],
    label: 'Identity Status',
  },
  trademarkStatus: {
    type: FilterType.Dropdown,
    options: [],
    label: 'Trademark Status',
  },
  loaStatus: {
    type: FilterType.Dropdown,
    options: [],
    label: 'LOA Status',
  },
};

const FilterLoaders = {
  suggestion: {
    displayName: {
      minLength: 2,
      load: (value: string) =>
        getBrandNameAutocompleteOptions(value, {
          status: 'ACTIVE',
        }),
    },
  },
};

const TableHeaders = [
  { id: 'uid', label: 'BRAND ID', sortable: true },
  { id: 'companyName', label: 'LEGAL NAME', sortable: true },
  { id: 'displayName', label: 'DBA NAME', sortable: false },
  { id: 'entityType', label: 'ENTITY TYPE', sortable: false },
  { id: 'identityStatus', label: 'IDENTITY STATUS', sortable: false },
  { id: 'trademarkStatus', label: 'TRADEMARK STATUS', sortable: false },
  { id: 'loaStatus', label: 'LOA STATUS', sortable: false },
];

const MAX_DISPLAY_RECORDS = 10000;

const BrandListing: React.FC = () => {
  const { profile } = useAuthContext();
  const {
    configs: filterConfigs,
    appliedFilters,
    candidateFilters,
    additionalFilters,
    setOptions,
    handleEdit,
    handleApply,
  } = useFilters({
    configs: FilterConfigs,
    loaders: FilterLoaders,
  });
  const {
    pageLoading,
    loading: tableLoading,
    filter: tableFilters,
    tableData: brands,
    pagination,
    handleSorting: handleSortingChange,
    handleChangePage: handlePageChange,
    reload,
  } = useTable<Brand>(
    async (tableFilters) => {
      const queries = {
        ...tableFilters,
        ...appliedFilters,
        ...additionalFilters,
      };
      const response = await brandController.getBrands({
        ...queries,
        ...additionalFilters,
        status: 'ACTIVE',
        market: profile?.market,
      });
      return {
        data: response?.records || [],
        total: response?.totalRecords || 0,
        rowsPerPage: response?.recordsPerPage || 10,
        page: response?.page || 1,
      };
    },
    {
      cappingTotal: MAX_DISPLAY_RECORDS,
      deps: [appliedFilters, additionalFilters],
      defaultFilter: {
        sortField: 'createDate',
        ascendingOrder: false,
      },
    }
  );
  const brandIdentityStatus = useSelector(selectBrandIdentityStatus);
  const loaStatus = useSelector(selectLoaStatus);
  const trademarkVerificationStatus = useSelector(
    selectTrademarkVerificationStatus
  );
  const history = useHistory();
  const [statistics, setStatistics] = useState<{
    notSubmittedTrademarks: number;
    signedLOAs: number;
    activeBrands: number;
  }>({
    notSubmittedTrademarks: 0,
    signedLOAs: 0,
    activeBrands: 0,
  });
  const [viewingBrand, setViewingBrand] = useState<string>();
  const [modalType, setModalType] = useState(ModalType.None);
  const [registeredBrandUid, setRegisteredBrandUid] = useState('');

  const fetchStatistics = async () => {
    const baseQuery = {
      status: 'ACTIVE',
      market: profile?.market,
    };
    const [activeBrandsResult, signedLoaResult, pendingTrademarkResult] =
      await Promise.all([
        brandController.getBrands({
          ...baseQuery,
        }),
        brandController.getBrands({
          ...baseQuery,
          loaStatus: 'COMPLETED',
        }),
        brandController.getBrands({
          ...baseQuery,
          trademarkStatus: 'NOT_SUBMITTED',
        }),
      ]);

    setStatistics((prev) => ({
      ...prev,
      activeBrands: activeBrandsResult?.totalRecords || 0,
      signedLOAs: signedLoaResult?.totalRecords || 0,
      notSubmittedTrademarks: pendingTrademarkResult?.totalRecords || 0,
    }));
  };

  const tableData = useMemo(() => {
    const wrap = (value: string, testId: string) =>
      value?.length > 0 ? (
        <Box
          sx={{
            maxWidth: '350px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          data-testid={testId}
        >
          {value}
        </Box>
      ) : null;

    const fallbackTo = (
      value: React.ReactNode,
      testId: string,
      fallbackValue = 'N/A'
    ) => value || <span data-testid={testId}>{fallbackValue}</span>;

    const renderStatus = (
      status: string | null | undefined,
      testId: string
    ) => {
      if (!status) return <span data-testid={testId}>N/A</span>;
      const type = getBadgeTypeByStatus(status);
      return (
        <Badge
          size="md"
          shape="rectangle"
          variant="fill"
          type={type}
          data-testid={testId}
        >
          {humanize(status)}
        </Badge>
      );
    };

    return brands.map((brand) => ({
      rawUid: brand.uid,
      uid: <span data-testid="uid">{brand.uid}</span>,
      companyName: fallbackTo(
        wrap(brand.companyName ?? '', 'companyName'),
        'companyName'
      ),
      displayName: fallbackTo(
        wrap(brand.displayName, 'displayName'),
        'displayName'
      ),
      entityType: fallbackTo(
        brand.entityType ? (
          <span data-testid="entityType">{humanize(brand.entityType)}</span>
        ) : null,
        'entityType'
      ),
      identityStatus: renderStatus(brand.identityStatus, 'identityStatus'),
      trademarkStatus: renderStatus(brand.trademarkStatus, 'trademarkStatus'),
      loaStatus: renderStatus(brand.loaStatus, 'loaStatus'),
    }));
  }, [brands]);

  const handleBrandAdd = () => {
    setModalType(ModalType.BrandRegistration);
  };

  useEffect(() => {
    fetchStatistics();
  }, []);

  useEffect(() => {
    setOptions(
      'identityStatus',
      brandIdentityStatus.map((item) => ({
        label: humanize(item),
        value: item,
      }))
    );
  }, [brandIdentityStatus]);

  useEffect(() => {
    setOptions(
      'loaStatus',
      loaStatus.map((item) => ({
        label: humanize(item),
        value: item,
      }))
    );
  }, [loaStatus]);

  useEffect(() => {
    setOptions(
      'trademarkStatus',
      trademarkVerificationStatus.map((item) => ({
        label: humanize(item),
        value: item,
      }))
    );
  }, [trademarkVerificationStatus]);

  if (pageLoading) return <Loader hocFlag />;

  return (
    <PageContainer
      sx={{
        paddingX: '96px',
        rowGap: null,
        '& .MuiAutocomplete-root': {
          width: 'unset',
        },
      }}
    >
      <Flex sx={{ justifyContent: 'space-between', width: '100%' }}>
        <PageTitle>Brands</PageTitle>
        <RoleGuard feature="brandList.addBrand">
          <Button
            size="lg"
            shape="square"
            variant="outline"
            color="secondary"
            startAdornment={faPlus}
            onClick={handleBrandAdd}
            data-testid="addBrand"
          >
            Add Brand
          </Button>
        </RoleGuard>
      </Flex>
      <Grid
        sx={{
          width: '100%',
          gap: '24px',
          gridTemplateColumns: 'repeat(4, 1fr)',
          marginTop: '26px',
          marginBottom: '56px',
        }}
      >
        <InsightCard
          title="TRADEMARKS NOT SUBMITTED"
          value={statistics.notSubmittedTrademarks}
          icon={faTrademark}
          data-testid="trademarksNotSubmitted"
        />
        <InsightCard
          title="SIGNED LOAs"
          value={statistics.signedLOAs}
          icon={faSignature}
          data-testid="signedLoas"
        />
        <InsightCard
          title="TOTAL ACTIVE BRANDS"
          value={statistics.activeBrands}
          icon={faFontAwesome}
          data-testid="totalActiveBrands"
        />
      </Grid>

      <Box sx={{ width: '100%', mb: 'm' }}>
        <TableFilter
          configs={filterConfigs}
          candidateValues={candidateFilters}
          appliedValues={appliedFilters}
          onCandidateValuesChange={handleEdit}
          onAppliedValuesChange={handleApply}
          data-testid="brandsTableFilter"
        />
      </Box>

      <Table
        onRowClick={(data) => setViewingBrand(data.rawUid as string)}
        loading={tableLoading}
        headRows={TableHeaders}
        emptyState="no brands to view"
        data={tableData}
        rowKey="uid"
        handleChangePage={handlePageChange}
        createSortHandler={handleSortingChange}
        filter={tableFilters}
        pagination={pagination}
        emptyBlockHeight="180px"
        testId="brandsTable"
      />

      <Drawer
        open={!!viewingBrand}
        initialScreenId="brandDetails"
        onClose={() => setViewingBrand(undefined)}
        width="70%"
      >
        <BrandDetailsV2
          brandUid={viewingBrand}
          onDeleteSuccess={() => {
            setViewingBrand(undefined);
            reload();
            fetchStatistics();
          }}
        />
      </Drawer>

      {modalType === ModalType.BrandRegistration && (
        <BrandRegistrationV2
          onCancel={() => setModalType(ModalType.None)}
          onSucceed={(brandUid) => {
            setRegisteredBrandUid(brandUid);
            setModalType(ModalType.BrandRegistered);
          }}
        />
      )}
      <BrandRegisteredModal
        open={modalType === ModalType.BrandRegistered}
        onNavigate={() => {
          history.push(`/brands/${registeredBrandUid}`, {
            created: true,
          });
          setModalType(ModalType.None);
        }}
        testId="brandRegisteredModal"
      />
    </PageContainer>
  );
};

export default BrandListing;
